<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
    <!-- Card Header -->
    <div class="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <div class="flex items-center space-x-2">
        <div
          role="status"
          :aria-label="statusAriaLabel"
          :class="[
            'w-2.5 h-2.5 rounded-full',
            statusIndicatorClass
          ]" />
        <h3 class="text-sm font-medium text-gray-900">
          Health Sync
        </h3>
      </div>
      <span v-if="syncStatus" class="text-xs text-gray-500">
        {{ connectionStatusText }}
      </span>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !syncStatus" class="p-4">
      <div class="animate-pulse space-y-3">
        <div class="h-4 bg-gray-200 rounded w-3/4" />
        <div class="h-3 bg-gray-200 rounded w-1/2" />
        <div class="flex space-x-4">
          <div class="h-8 bg-gray-200 rounded w-1/3" />
          <div class="h-8 bg-gray-200 rounded w-1/3" />
          <div class="h-8 bg-gray-200 rounded w-1/3" />
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error && !syncStatus" class="p-4">
      <div class="text-center py-4">
        <svg class="w-8 h-8 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-sm text-gray-600">
          {{ error }}
        </p>
        <button
          @click="loadSyncStatus"
          class="mt-2 text-sm text-gray-900 font-medium hover:underline">
          Try again
        </button>
      </div>
    </div>

    <!-- Content -->
    <div v-else-if="syncStatus" :aria-busy="syncing">
      <!-- Syncing Progress -->
      <div
        v-if="syncing"
        aria-live="polite"
        class="p-4 bg-blue-50 border-b border-blue-100">
        <SyncProgressIndicator
          :progress="syncProgress"
          :current-category="currentCategory"
          :samples-received="samplesReceived"
          :completed-categories="completedCategories" />
      </div>

      <!-- Quick Stats (when connected and has data) -->
      <div v-if="syncStatus.connected && syncStatus.quick_stats" class="p-4">
        <div class="grid grid-cols-3 gap-4 text-center">
          <div v-if="syncStatus.quick_stats.steps_today !== null">
            <p class="text-lg font-semibold text-gray-900">
              {{ formatNumber(syncStatus.quick_stats.steps_today) }}
            </p>
            <p class="text-xs text-gray-500">
              Steps Today
            </p>
          </div>
          <div v-if="syncStatus.quick_stats.last_heart_rate !== null">
            <p class="text-lg font-semibold text-gray-900">
              {{ syncStatus.quick_stats.last_heart_rate }}
            </p>
            <p class="text-xs text-gray-500">
              Heart Rate
            </p>
          </div>
          <div v-if="syncStatus.quick_stats.last_sleep !== null">
            <p class="text-lg font-semibold text-gray-900">
              {{ formatSleep(syncStatus.quick_stats.last_sleep) }}
            </p>
            <p class="text-xs text-gray-500">
              Last Sleep
            </p>
          </div>
        </div>

        <!-- Stale Data Warning -->
        <div
          v-if="syncStatus.is_stale"
          class="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800 text-center">
          Data may be outdated. Last sync was {{ formatTimeAgo(syncStatus.last_sync_at) }}.
        </div>
      </div>

      <!-- Not Connected State -->
      <div v-else-if="!syncStatus.connected" class="p-4 text-center">
        <svg class="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <p class="text-sm text-gray-600">
          Not connected to Apple Health
        </p>
        <a
          href="https://apps.apple.com/app/your-app"
          class="mt-2 inline-block text-sm text-gray-900 font-medium hover:underline">
          Get the iOS App
        </a>
      </div>

      <!-- Request Sync Button -->
      <div class="px-4 py-3 bg-gray-50 border-t border-gray-100">
        <div class="flex items-center justify-between">
          <!-- Expand Details Button -->
          <button
            v-if="syncStatus.connected"
            @click="showDetails = !showDetails"
            :aria-expanded="showDetails"
            aria-controls="sync-details-panel"
            class="text-xs text-gray-500 hover:text-gray-700 flex items-center">
            <svg
              :class="['w-4 h-4 mr-1 transition-transform', showDetails ? 'rotate-180' : '']"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
            {{ showDetails ? 'Hide details' : 'View sync details' }}
          </button>
          <div v-else />

          <!-- Request Sync Button -->
          <button
            @click="handleRequestSync"
            :disabled="!canRequestSync"
            :title="syncButtonTooltip"
            class="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors
                   text-white bg-gray-900 hover:bg-gray-800
                   disabled:bg-gray-300 disabled:cursor-not-allowed">
            <svg
              :class="['w-4 h-4 mr-1', syncing ? 'animate-spin' : '']"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {{ syncButtonText }}
          </button>
        </div>

        <!-- Pending Request Countdown -->
        <div v-if="pendingCountdown" class="mt-2 text-xs text-gray-500 text-right">
          Request expires in {{ pendingCountdown }}
        </div>

        <!-- Error Message -->
        <div v-if="syncError" class="mt-2 text-xs text-red-600 text-right">
          {{ syncError }}
        </div>
      </div>

      <!-- Expandable Details Panel -->
      <transition name="slide">
        <SyncHistoryPanel
          v-if="showDetails"
          id="sync-details-panel"
          :category-status="syncStatus.category_status"
          :recent-events="syncStatus.recent_events"
          :total-samples="syncStatus.total_samples" />
      </transition>
    </div>

    <!-- Cached Data Indicator -->
    <div v-if="usingCachedData" class="px-4 py-2 bg-yellow-50 text-xs text-yellow-700 text-center">
      Showing cached data from {{ formatTimeAgo(cachedAt) }}
    </div>
  </div>
</template>

<script>
import consumer from '~/channels/consumer'
import { getHealthSyncSummary, requestSync, clearSyncCache } from '~/javascript/api/health_sync'
import SyncProgressIndicator from './sync_progress_indicator.vue'
import SyncHistoryPanel from './sync_history_panel.vue'

export default {
  name: 'HealthSyncCard',

  components: {
    SyncProgressIndicator,
    SyncHistoryPanel
  },

  props: {
    userId: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      syncStatus: null,
      loading: true,
      error: null,
      syncing: false,
      syncProgress: 0,
      currentCategory: null,
      samplesReceived: 0,
      completedCategories: [],
      showDetails: false,
      syncError: null,
      usingCachedData: false,
      cachedAt: null,
      pendingCountdown: null,
      subscription: null,
      pollingInterval: null,
      refreshInterval: null,
      countdownInterval: null
    }
  },

  computed: {
    statusIndicatorClass() {
      if (this.syncing) return 'bg-blue-500 animate-pulse'
      if (!this.syncStatus) return 'bg-gray-300'
      if (!this.syncStatus.connected) return 'bg-gray-300'
      if (this.syncStatus.is_stale) return 'bg-yellow-500'
      return 'bg-green-500'
    },

    statusAriaLabel() {
      if (this.syncing) return 'Syncing health data'
      if (!this.syncStatus) return 'Loading sync status'
      if (!this.syncStatus.connected) return 'Not connected to Apple Health'
      if (this.syncStatus.is_stale) return 'Health data may be outdated'
      return 'Connected to Apple Health'
    },

    connectionStatusText() {
      if (this.syncing) return 'Syncing...'
      if (!this.syncStatus?.connected) return 'Not Connected'
      if (this.syncStatus.is_stale) return 'Needs Sync'
      return 'Connected'
    },

    canRequestSync() {
      if (this.syncing) return false
      if (!this.syncStatus) return false
      return this.syncStatus.can_request_sync
    },

    syncButtonText() {
      if (this.syncing) return 'Syncing...'
      if (this.syncStatus?.pending_request) return 'Pending...'
      return 'Request Sync'
    },

    syncButtonTooltip() {
      if (this.syncing) return 'Sync in progress'
      if (!this.syncStatus?.has_device_token) {
        return 'Enable push notifications in the iOS app'
      }
      if (this.syncStatus?.pending_request) {
        return 'A sync request is already pending'
      }
      return 'Request a sync from your iOS device'
    }
  },

  mounted() {
    this.loadSyncStatus()
    this.setupActionCable()
    this.setupAutoRefresh()
  },

  beforeUnmount() {
    this.cleanup()
  },

  methods: {
    async loadSyncStatus() {
      this.loading = true
      this.error = null

      try {
        const result = await getHealthSyncSummary()
        this.syncStatus = result.data
        this.usingCachedData = result.cached || false
        this.cachedAt = result.cachedAt || null

        // Start pending request countdown if applicable
        if (this.syncStatus.pending_request) {
          this.startPendingCountdown(this.syncStatus.pending_request.expires_at)
        }
      } catch (err) {
        this.error = err.message || 'Failed to load sync status'
      } finally {
        this.loading = false
      }
    },

    async handleRequestSync() {
      if (!this.canRequestSync) return

      this.syncError = null

      const result = await requestSync()

      if (result.success) {
        // Update local state
        this.syncStatus.can_request_sync = false
        this.syncStatus.pending_request = {
          id: result.requestId,
          expires_at: result.expiresAt
        }
        this.startPendingCountdown(result.expiresAt)
        clearSyncCache()
      } else {
        this.syncError = result.message

        // Clear error after 5 seconds
        setTimeout(() => {
          this.syncError = null
        }, 5000)
      }
    },

    setupActionCable() {
      try {
        this.subscription = consumer.subscriptions.create(
          { channel: 'SyncStatusChannel' },
          {
            connected: () => {
              // Stop polling when ActionCable connects
              this.stopPolling()
            },
            disconnected: () => {
              // Start polling fallback when ActionCable disconnects
              this.startPolling()
            },
            received: (data) => {
              this.handleSyncEvent(data)
            }
          }
        )
      } catch {
        // ActionCable not available, use polling
        this.startPolling()
      }
    },

    handleSyncEvent(data) {
      switch (data.event) {
        case 'sync_requested':
          this.syncStatus.can_request_sync = false
          this.syncStatus.pending_request = {
            id: data.request_id,
            expires_at: data.expires_at
          }
          this.startPendingCountdown(data.expires_at)
          break

        case 'sync_started':
          this.syncing = true
          this.syncProgress = 0
          this.currentCategory = null
          this.samplesReceived = 0
          this.completedCategories = []
          break

        case 'sync_progress':
          this.syncing = true
          this.syncProgress = data.percentage || 0
          this.currentCategory = data.current_category
          this.samplesReceived = data.samples_received || 0
          this.completedCategories = data.completed_categories || []
          break

        case 'sync_completed':
          this.syncing = false
          this.syncProgress = 100
          this.stopPendingCountdown()
          // Refresh data to get updated stats
          clearSyncCache()
          this.loadSyncStatus()
          break

        case 'sync_failed':
          this.syncing = false
          this.syncError = data.error || 'Sync failed'
          this.stopPendingCountdown()
          clearSyncCache()
          this.loadSyncStatus()
          break
      }
    },

    startPolling() {
      if (this.pollingInterval) return

      this.pollingInterval = setInterval(() => {
        this.loadSyncStatus()
      }, 30000) // Poll every 30 seconds
    },

    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval)
        this.pollingInterval = null
      }
    },

    setupAutoRefresh() {
      // Auto-refresh every 60 seconds when tab is visible
      this.refreshInterval = setInterval(() => {
        if (document.visibilityState === 'visible' && !this.syncing) {
          this.loadSyncStatus()
        }
      }, 60000)
    },

    startPendingCountdown(expiresAt) {
      this.stopPendingCountdown()

      const updateCountdown = () => {
        const now = new Date()
        const expires = new Date(expiresAt)
        const diffMs = expires - now

        if (diffMs <= 0) {
          this.pendingCountdown = null
          this.stopPendingCountdown()
          // Refresh to get updated state
          clearSyncCache()
          this.loadSyncStatus()
          return
        }

        const diffSecs = Math.floor(diffMs / 1000)
        const mins = Math.floor(diffSecs / 60)
        const secs = diffSecs % 60
        this.pendingCountdown = `${mins}:${secs.toString().padStart(2, '0')}`
      }

      updateCountdown()
      this.countdownInterval = setInterval(updateCountdown, 1000)
    },

    stopPendingCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
      this.pendingCountdown = null
    },

    cleanup() {
      if (this.subscription) {
        this.subscription.unsubscribe()
        this.subscription = null
      }
      this.stopPolling()
      this.stopPendingCountdown()
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },

    formatNumber(num) {
      if (num === null || num === undefined) return '--'
      return num.toLocaleString()
    },

    formatSleep(hours) {
      if (hours === null || hours === undefined) return '--'
      const h = Math.floor(hours)
      const m = Math.round((hours - h) * 60)
      return `${h}h ${m}m`
    },

    formatTimeAgo(dateString) {
      if (!dateString) return 'Never'

      const date = new Date(dateString)
      const now = new Date()
      const diffMs = now - date
      const diffSecs = Math.floor(diffMs / 1000)
      const diffMins = Math.floor(diffSecs / 60)
      const diffHours = Math.floor(diffMins / 60)
      const diffDays = Math.floor(diffHours / 24)

      if (diffSecs < 60) return 'just now'
      if (diffMins < 60) return `${diffMins}m ago`
      if (diffHours < 24) return `${diffHours}h ago`
      if (diffDays === 1) return 'yesterday'
      if (diffDays < 7) return `${diffDays}d ago`
      return date.toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
  transition: all 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-enter-to,
.slide-leave-from {
  max-height: 500px;
}
</style>
